import { useForm as createUseForm } from "@ant-design-vue/use";
import lodash from "lodash";
import { defineAsyncComponent, reactive, toRaw } from "vue";
import { addModal } from "../../../../../../app.logic";
import {
    createFieldRadioGroup,
    createFieldTextArea
} from "../../../../../../components/input/template-builder/template-builder.logic";
import i18n from "../../../../../../i18n";
import { modalStyleForm } from "../../../../../../utils/constant";
import { conditionFormChecker, initUseForm } from "../../../../../../utils/form";
import { createLoading } from "../../../../../../utils/loading";
import { required, validateError } from "../../../../../../utils/validate";
import { callRequestStatus } from "../../../../storage-location/request/request.section";

export const { isLoading, setIsLoading } = createLoading();

export const ConfirmApproveForm = defineAsyncComponent(() => import("./ConfirmApproveForm"));

export const openModalForm = ({ props, onOk, bind }) => {
    const refs = reactive({ form: null });
    const options = addModal({
        bind: lodash.merge(
            lodash.cloneDeep(modalStyleForm),
            {
                okButtonProps: { disabled: false },
                title: i18n.global.t("views.request.modal.approve.title"),
                width: "500px",
                onOk: () => {
                    refs.form.callOnClickModalOk().then(record => {
                        onOk?.(record);
                        options.bind.onCancel();
                    });
                }
            },
            bind
        ),
        render: () => (
            <ConfirmApproveForm
                ref={e => {
                    if (e) refs.form = e;
                }}
                {...props}
            ></ConfirmApproveForm>
        )
    });
    return options;
};

export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef) }))
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const createForm = () => {
    return {
        refStatus: null
    };
};

export const createRules = () => {
    return {
        refStatus: [required]
    };
};

export const createField = ({ props, data }) => {
    const { routerPrefix } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    return [
        createFieldRadioGroup({
            key: "refStatus",
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24 }, wrapperCol: { xs: 24 } }),
            inputProp: () => {
                return {
                    value: modelRef.refStatus,
                    "onUpdate:value": value => {
                        modelRef.refStatus = value
                        if (value?.key !== "notApprove") {
                            modelRef.remark = null;
                        }
                    },
                    list: ({ searchQuery }) =>
                        callRequestStatus({ searchQuery }).then(items => {
                            const result = [];
                            const waitRelease = items.find(item => item.key === "waitRelease");
                            const approve = items.find(item => item.key === "approve");
                            const notApprove = items.find(item => item.key === "notApprove");
                            const reEditAuthority = items.find(item => item.key === "reEditAuthority");
                            if (routerPrefix === "release-results" && waitRelease) result.push(waitRelease);
                            if (routerPrefix !== "release-results" && approve) result.push(approve);
                            if (reEditAuthority) result.push(reEditAuthority);
                            if ([
                                "release-requests",
                                "sampling-changes",
                                "sequestrate-changes",
                                "open-inspection-changes",
                                "release-cancels",
                                "send-samplings",
                                "send-sampling-cancels",
                                "sampling-analysiss",
                                "sampling-analysis-cancels",
                                "cancel-sequestrate-cancels",
                            ].includes(routerPrefix) && notApprove) result.push(notApprove);
                            return result;
                        }),
                    renderLabel: ({ item }) => {
                        return <span>{item.attribute?.actionText2 || item.attribute?.actionText}</span>
                    },
                    type: "vertical"
                };
            }
        }),
        createFieldTextArea({
            visible: () => ["notApprove"].includes(modelRef.refStatus?.key),
            key: `remark`,
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t("views.request.releaseInfo.remark"),
            inputProp: ({ field }) =>
                lodash.merge(
                    {
                        value: modelRef.remark,
                        "onUpdate:value": value => (modelRef.remark = value),
                    },
                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                )
        }),
    ];
};

export const loadForm = ({ props, data }) => {
    setIsLoading(true);
    const { form } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    let promise = null;
    if (form) {
        promise = Promise.resolve({ data: [form] });
    } else {
        promise = Promise.resolve({ data: [modelRef] });
    }
    return promise.then(page => initUseForm({ data, page })).finally(() => setIsLoading(false));
};

export const initForm = ({ props, data, refs }) => {
    data.form = createForm({ props, data, refs });
    data.rules = createRules({ props, data, refs });
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs });
    loadForm({ props, data, refs });
};
